import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { useToast } from '@/components/Toast';
import {
  billingAdminService,
  type BillingEntitlement,
  type BillingPlan,
  type OrgUsageSummary,
} from '@/api/services/billingAdmin';
import { billingService } from '@/api/services/billing';
import { adminBillingService } from '@/api/services/adminBilling';
import { type SubscriptionStatus } from '@/api/services/notifications';
import { useMe } from '@/context/MeContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { formatApiError } from '@/utils/formatApiError';
import { invalidatePlanDependentCaches, usePlanChangeListener } from '@/utils/planRefresh';
import { ApiError } from '@/api/http';
import { formatPlanDisplayName } from '@/utils/planDisplayName';
import { LIFECYCLE_STATE_LABELS } from '@/utils/lifecycleReasons';
import { DataTableScrollArea } from '@/components/table';

type Props = {
  usage: OrgUsageSummary;
  subscription?: SubscriptionStatus | null;
  onChanged?: () => void;
  compact?: boolean;
};

export default function OrgSubscriptionPanel({ usage, subscription, onChanged, compact }: Props) {
  const { me } = useMe();
  const { invalidateWorkspaces } = useWorkspace();
  const { toast } = useToast();
  const canManagePlatform =
    (me?.permissions.canManageAdminBilling ?? false) ||
    (me?.permissions.canRunManualBillingOps ?? false);
  const canRunLifecycle = me?.permissions.canRunManualBillingOps ?? false;
  const canCheckout = me?.permissions.canUseBillingCheckout ?? false;
  const orgId = usage.organizationId;

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [entitlements, setEntitlements] = useState<BillingEntitlement[]>([]);
  const [pendingGrantPlan, setPendingGrantPlan] = useState<string | null>(null);
  const [grantExpiry, setGrantExpiry] = useState('');
  const [extendExpiry, setExtendExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [confirmLifecycle, setConfirmLifecycle] = useState(false);
  const [onlinePaymentsAvailable, setOnlinePaymentsAvailable] = useState(false);

  const effectiveGrantPlan = useMemo(
    () => String(usage.planSlug || 'starter').trim().toLowerCase(),
    [usage.planSlug],
  );
  const grantPlan = pendingGrantPlan ?? effectiveGrantPlan;

  const reloadEntitlements = useCallback(async () => {
    if (!canManagePlatform || !orgId) {
      setEntitlements([]);
      return;
    }
    const res = await billingAdminService.listEntitlements({
      organizationId: orgId,
      activeOnly: false,
    });
    setEntitlements(res.items);
  }, [canManagePlatform, orgId]);

  useEffect(() => {
    if (!canManagePlatform) return;
    billingAdminService
      .listPlans(true)
      .then((res) => {
        setPlans(res.items.filter((p) => p.isActive));
      })
      .catch(() => {});
    reloadEntitlements().catch(() => {});
  }, [canManagePlatform, reloadEntitlements]);

  useEffect(() => {
    if (!canCheckout) {
      setOnlinePaymentsAvailable(false);
      return;
    }
    billingService
      .status()
      .then((status) => setOnlinePaymentsAvailable(Boolean(status.onlinePaymentsAvailable)))
      .catch(() => setOnlinePaymentsAvailable(false));
  }, [canCheckout]);

  useEffect(() => {
    setPendingGrantPlan(null);
  }, [effectiveGrantPlan]);

  usePlanChangeListener(() => {
    void reloadEntitlements();
    onChanged?.();
  });

  const afterChange = async (grantedPlanSlug?: string) => {
    if (grantedPlanSlug) {
      setPendingGrantPlan(grantedPlanSlug.trim().toLowerCase());
    }
    await reloadEntitlements();
    await invalidatePlanDependentCaches({ invalidateWorkspaces });
    onChanged?.();
  };

  const handleGrant = async (planSlug?: string) => {
    setBusy(true);
    setError(null);
    const slug = (planSlug ?? grantPlan).trim().toLowerCase();
    try {
      const granted = await billingAdminService.grantEntitlement({
        organizationId: orgId,
        planSlug: slug,
        expiresAt: grantExpiry || undefined,
        reason: 'ui_grant',
      });
      setGrantExpiry('');
      await afterChange(String(granted.planSlug || slug));
      toast('Plan updated', 'success');
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const executeRevoke = async (entitlementId: string) => {
    setBusy(true);
    setError(null);
    try {
      await billingAdminService.revokeEntitlement(entitlementId, 'ui_revoke');
      toast('Plan revoked', 'success');
      await afterChange();
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast(message, 'error');
    } finally {
      setBusy(false);
      setRevokeTarget(null);
    }
  };

  const executeLifecycle = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminBillingService.manualOp({
        operation: 'run_lifecycle',
        confirm: true,
        reason: 'ui_lifecycle',
      });
      toast('Lifecycle run queued', 'success');
      await afterChange();
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast(message, 'error');
    } finally {
      setBusy(false);
      setConfirmLifecycle(false);
    }
  };

  const handleExtend = async () => {
    if (!extendExpiry.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (canRunLifecycle) {
        await adminBillingService.manualOp({
          operation: 'grant_extension',
          organizationId: orgId,
          expiresAt: extendExpiry.trim(),
          planSlug: usage.planSlug || grantPlan,
          confirm: true,
          reason: 'ui_extend',
        });
      } else {
        await billingAdminService.grantEntitlement({
          organizationId: orgId,
          planSlug: usage.planSlug || grantPlan,
          expiresAt: extendExpiry.trim(),
          reason: 'ui_extend',
        });
      }
      setExtendExpiry('');
      await afterChange();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleLifecycle = async () => {
    setConfirmLifecycle(true);
  };

  const handleCheckout = async (plan: string) => {
    setCheckoutBusy(true);
    setError(null);
    try {
      const { checkoutUrl } = await billingService.checkout(plan);
      window.location.href = checkoutUrl;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Checkout failed');
      setCheckoutBusy(false);
    }
  };

  const handlePortal = async () => {
    setCheckoutBusy(true);
    setError(null);
    try {
      const { portalUrl } = await billingService.portal();
      window.location.href = portalUrl;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Portal failed');
      setCheckoutBusy(false);
    }
  };

  const planLabel = usage.unlimited
    ? 'Internal (Unlimited)'
    : formatPlanDisplayName(usage.planSlug);
  const activeEntitlements = entitlements.filter((e) =>
    ['active', 'manual_grant'].includes(e.status),
  );

  return (
    <section
      className="panel org-subscription-panel"
      id="manage-subscription"
      aria-label={compact ? 'Subscription status' : undefined}
    >
      {!compact ? <h2>Manage subscription</h2> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="usage-summary-grid">
        <p>
          Current plan: <strong>{planLabel}</strong>
        </p>
        {subscription ? (
          <p className="muted">
            Lifecycle:{' '}
            <strong>
              {LIFECYCLE_STATE_LABELS[subscription.lifecycleState] ?? subscription.lifecycleLabel}
            </strong>
            {subscription.expiresAt
              ? ` · expires ${new Date(subscription.expiresAt).toLocaleDateString()}`
              : ''}
          </p>
        ) : usage.expiresAt ? (
          <p className="muted">Expires {new Date(usage.expiresAt).toLocaleDateString()}</p>
        ) : null}
      </div>

      {canCheckout && onlinePaymentsAvailable ? (
        <div className="table-action-bar org-subscription-panel__checkout">
          {plans
            .filter((p) => p.slug !== 'internal' && p.slug !== usage.planSlug)
            .slice(0, 3)
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className="btn btn-sm btn-primary"
                disabled={checkoutBusy || busy}
                onClick={() => void handleCheckout(p.slug)}
              >
                Upgrade to {p.name}
              </button>
            ))}
          <button
            type="button"
            className="btn btn-sm"
            disabled={checkoutBusy || busy}
            onClick={() => void handlePortal()}
          >
            Manage billing
          </button>
        </div>
      ) : null}

      {canManagePlatform ? (
        <>
          <section className="billing-form-card org-subscription-panel__change-plan">
            <h3 className="billing-form-card__title">Change plan</h3>
            <div className="billing-form-grid">
              <label className="billing-form-grid__field">
                Plan
                <select value={grantPlan} onChange={(e) => setPendingGrantPlan(e.target.value)}>
                  {plans.map((p) => (
                    <option key={p.id} value={p.slug}>
                      {p.name} ({p.slug})
                    </option>
                  ))}
                </select>
              </label>
              <label className="billing-form-grid__field">
                Expiry date
                <input
                  type="datetime-local"
                  value={grantExpiry}
                  onChange={(e) => setGrantExpiry(e.target.value)}
                />
                <span className="muted admin-billing-field-hint">Optional. Leave blank for no expiry.</span>
              </label>
              <div className="billing-form-grid__actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => void handleGrant()}
                >
                  Grant / change plan
                </button>
                {plans.some((p) => p.slug === 'internal') ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => void handleGrant('internal')}
                  >
                    Grant internal
                  </button>
                ) : null}
              </div>
            </div>
          </section>

          <section className="billing-form-card org-subscription-panel__extend">
            <h3 className="billing-form-card__title">Extend expiry</h3>
            <div className="billing-form-grid billing-form-grid--single">
              <label className="billing-form-grid__field">
                New expiry
                <input
                  value={extendExpiry}
                  onChange={(e) => setExtendExpiry(e.target.value)}
                  placeholder="2026-12-31T00:00:00Z"
                />
              </label>
              <div className="billing-form-grid__actions">
                <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void handleExtend()}>
                  Extend subscription
                </button>
                {canRunLifecycle ? (
                  <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void handleLifecycle()}>
                    Run lifecycle
                  </button>
                ) : null}
              </div>
            </div>
          </section>

          {canManagePlatform && activeEntitlements.length > 0 ? (
            <div>
              <h3>Active entitlements</h3>
              <DataTableScrollArea>
        <table className="data-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Expires</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {activeEntitlements.map((e) => (
                    <tr key={e.id}>
                      <td>{e.planName ?? e.planSlug ?? '—'}</td>
                      <td>{e.status}</td>
                      <td>{e.source}</td>
                      <td>{e.expiresAt ? new Date(e.expiresAt).toLocaleString() : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => setRevokeTarget(e.id)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
        </DataTableScrollArea>
            </div>
          ) : null}
        </>
      ) : canCheckout ? (
        <p className="muted">
          Online payments are not currently available. Contact Scoutly support for plan changes.
        </p>
      ) : (
        <p className="muted">
          Contact your workspace admin to change the subscription plan.
        </p>
      )}

      {me?.permissions.canViewAdminBilling && orgId ? (
        <p className="muted">
          <Link to={`/admin/billing/organizations/${orgId}`}>Open admin billing console</Link>
        </p>
      ) : null}
      <ConfirmActionModal
        open={Boolean(revokeTarget)}
        title="Revoke plan?"
        description="Active searches for this organization may be paused."
        confirmLabel="Revoke"
        variant="danger"
        loading={busy}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => (revokeTarget ? executeRevoke(revokeTarget) : undefined)}
      />
      <ConfirmActionModal
        open={confirmLifecycle}
        title="Run subscription lifecycle?"
        description="This will run lifecycle processing for all organizations."
        confirmLabel="Run lifecycle"
        loading={busy}
        onCancel={() => setConfirmLifecycle(false)}
        onConfirm={executeLifecycle}
      />
    </section>
  );
}
